import { Flex } from "@mantine/core";
import Loader from "@/components/ui/Loader/Loader";
import type { CSSProperties, ReactNode } from "react";

import { Modal, type ModalProps } from "../Modal/Modal";
import { Button, type ButtonVariant } from "../Button/Button";
import styles from "./ConfirmationModal.module.scss";

// Inline, not a class: Button replaces its own variant classes with any className passed in.
// Keeps the width while the label turns into a spinner.
const CONFIRM_BUTTON_STYLE: CSSProperties = { minWidth: "100px" };

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  body: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  isConfirmDisabled?: boolean;
  isConfirmLoading?: boolean;
  size?: ModalProps["size"];
  confirmVariant?: ButtonVariant;
  cancelVariant?: ButtonVariant;
  /** For a modal that only explains why something can't be done: the cancel button alone. */
  hideConfirm?: boolean;
  zIndex?: number;
}

export function ConfirmationModal({
  isOpen,
  onClose,
  title,
  body,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  isConfirmDisabled,
  isConfirmLoading,
  size = "lg",
  confirmVariant = "primary",
  cancelVariant = "neutral-secondary",
  hideConfirm = false,
  zIndex,
}: ConfirmationModalProps) {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      closeOnClickOutside={false}
      closeOnEscape={true}
      contentClassName={size === "lg" ? styles.confirmationModalContent : undefined}
      zIndex={zIndex}
    >
      <Flex className={styles.confirmationModalRoot}>
        <Flex className={styles.confirmationModalBody}>{body}</Flex>
        <Flex className={styles.confirmationModalActionsRow}>
          <Button
            variant={cancelVariant}
            size="large"
            onClick={handleCancel}
            isDisabled={isConfirmLoading}
          >
            {cancelText}
          </Button>
          {!hideConfirm && (
            <Button
              variant={confirmVariant}
              size="large"
              onClick={handleConfirm}
              isDisabled={isConfirmDisabled || isConfirmLoading}
              style={CONFIRM_BUTTON_STYLE}
            >
              {isConfirmLoading ? <Loader size="small" withContainer={false} /> : confirmText}
            </Button>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
}
